import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const RegisterPage = () => {
  const [step, setStep] = useState(1); // 1: name, 2: email, 3: password
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleNext = (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!name || name.length < 2) {
        toast.error("Bitte geben Sie Ihren Namen ein (mindestens 2 Zeichen)");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!email) {
        toast.error("Bitte geben Sie Ihre E-Mail ein");
        return;
      }
      setStep(3);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      toast.error("Das Kennwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Die Kennwörter stimmen nicht überein");
      return;
    }

    setIsLoading(true);
    try {
      await register(name, email, password);
      toast.success("Konto erfolgreich erstellt!");
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Registrierung fehlgeschlagen";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="register-page">
      {/* Background with abstract shapes */}
      <div className="fixed inset-0 bg-[#f2f2f2] -z-10">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#c3d7e8" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#a8d4e6" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="grad2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#d4e5f0" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#b8d4e8" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <polygon points="0,400 600,200 400,600 0,800" fill="url(#grad1)" />
          <polygon points="1920,0 1400,300 1600,700 1920,400" fill="url(#grad2)" />
          <polygon points="800,900 1200,700 1100,1080 700,1080" fill="url(#grad1)" />
        </svg>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-[440px]">
          {/* Register Card */}
          <div className="bg-white shadow-[0_2px_6px_rgba(0,0,0,0.2)] p-11">
            {/* Logo */}
            <div className="mb-4 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_flexipfleg-login/artifacts/d5s7aion_image.png" 
                alt="Holding" 
                className="h-14"
                data-testid="holding-logo"
              />
            </div>

            {step > 1 && (
              <button 
                onClick={handleBack}
                className="flex items-center text-sm text-[#1b1b1b] hover:underline mb-4"
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Zurück
              </button>
            )}

            <h1 className="text-2xl font-semibold text-[#1b1b1b] mb-4" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
              {step === 1 && "Konto erstellen"}
              {step === 2 && "E-Mail hinzufügen"}
              {step === 3 && "Kennwort erstellen"}
            </h1>

            {step === 1 && (
              <form onSubmit={handleNext}>
                <div className="mb-4">
                  <Input
                    type="text"
                    placeholder="Vollständiger Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border-0 border-b border-[#666] rounded-none px-0 h-9 text-[15px] focus:border-b-[#0067b8] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#666]"
                    autoFocus
                    data-testid="register-name-input"
                  />
                </div>

                <p className="text-sm text-[#1b1b1b] mb-6">
                  Sie haben bereits ein Konto?{" "}
                  <Link to="/login" className="text-[#0067b8] hover:text-[#005a9e] hover:underline" data-testid="login-link">
                    Jetzt anmelden
                  </Link>
                </p>

                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    className="bg-[#0067b8] hover:bg-[#005a9e] text-white rounded-none px-8 h-9 text-[15px] font-normal"
                    data-testid="register-next-button"
                  >
                    Weiter
                  </Button>
                </div>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleNext}>
                <p className="text-sm text-[#666] mb-4">
                  Geben Sie Ihre E-Mail-Adresse ein
                </p>
                <div className="mb-6">
                  <Input
                    type="email"
                    placeholder="name@beispiel.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border-0 border-b border-[#666] rounded-none px-0 h-9 text-[15px] focus:border-b-[#0067b8] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#666]"
                    autoFocus
                    data-testid="register-email-input"
                  />
                </div>

                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    className="bg-[#0067b8] hover:bg-[#005a9e] text-white rounded-none px-8 h-9 text-[15px] font-normal"
                    data-testid="register-next-button-2"
                  >
                    Weiter
                  </Button>
                </div>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleSubmit}>
                <p className="text-sm text-[#666] mb-4">
                  Erstellen Sie ein sicheres Kennwort (mind. 6 Zeichen)
                </p>
                <div className="mb-4">
                  <Input
                    type="password"
                    placeholder="Kennwort erstellen"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border-0 border-b border-[#666] rounded-none px-0 h-9 text-[15px] focus:border-b-[#0067b8] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#666]"
                    autoFocus
                    data-testid="register-password-input"
                  />
                </div>
                <div className="mb-6">
                  <Input
                    type="password"
                    placeholder="Kennwort bestätigen"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full border-0 border-b border-[#666] rounded-none px-0 h-9 text-[15px] focus:border-b-[#0067b8] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[#666]"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    className="bg-[#0067b8] hover:bg-[#005a9e] text-white rounded-none px-8 h-9 text-[15px] font-normal"
                    disabled={isLoading}
                    data-testid="register-submit-button"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Konto erstellen"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end items-center p-4 text-xs text-[#666] space-x-6">
        <a href="#" className="hover:underline">Nutzungsbedingungen</a>
        <a href="#" className="hover:underline">Datenschutz und Cookies</a>
        <button className="hover:underline">...</button>
      </div>
    </div>
  );
};

export default RegisterPage;
